import { getClientBuildCommit } from "@/lib/buildIdentity";

/** Allowlisted surface identifiers for Sales issue reports. */
export type SalesIssueSurface =
  | "sales.my_proposals.active"
  | "sales.my_proposals.archived"
  | "sales.my_proposals.kpi_drilldown"
  | "sales.my_proposals.dashboard_support"
  | "sales.projects.list"
  | "sales.customer_log.list"
  | "sales.dashboard.summary"
  | "sales.dashboard.cleo_summary"
  | "sales.my_proposals.won_reconciliation"
  | "sales.tranche_invoice.generation_side_effect"
  | "sales.customer_card.service_sites"
  | "sales.customer_search";

export type SalesSafeIssuePayloadInput = {
  surface: SalesIssueSurface;
  /** Safe TRPC error code / category only (e.g. INTERNAL_SERVER_ERROR, FORBIDDEN). */
  safeErrorCode: string;
  priorDataRetained: boolean;
  /** Same-origin pathname only; never a full URL with query secrets. */
  pathname?: string;
  buildCommit?: string | null;
  allowlistedProposalRef?: string | null;
  allowlistedInvoiceRefs?: readonly string[] | null;
  allowlistedOutstandingSgd?: number | null;
};

const SURFACE_TITLE: Record<SalesIssueSurface, string> = {
  "sales.my_proposals.active": "Sales — My Proposals (active list)",
  "sales.my_proposals.archived": "Sales — My Proposals (archive list)",
  "sales.my_proposals.kpi_drilldown": "Sales — My Proposals (KPI drilldown)",
  "sales.my_proposals.dashboard_support": "Sales — My Proposals (dashboard filter support)",
  "sales.projects.list": "Sales — Projects list",
  "sales.customer_log.list": "Sales — Customer Log list",
  "sales.dashboard.summary": "Sales — Dashboard summary",
  "sales.dashboard.cleo_summary": "Sales — Dashboard Cleo summary",
  "sales.my_proposals.won_reconciliation": "Sales — Won payment reconciliation",
  "sales.tranche_invoice.generation_side_effect": "Sales — Tranche invoice created, follow-up failed",
  "sales.customer_card.service_sites": "Sales — Customer Card service sites",
  "sales.customer_search": "Sales — Customer directory search",
};

function safeToken(raw: string | null | undefined, max = 64): string {
  return String(raw ?? "")
    .replace(/[^A-Za-z0-9_./:-]/g, "")
    .trim()
    .slice(0, max);
}

function safePathname(raw: string | null | undefined): string {
  const s = String(raw ?? "").trim();
  if (!s.startsWith("/")) return "/";
  // Strip query/hash; keep path segments only.
  const pathOnly = s.split("?")[0]?.split("#")[0] ?? "/";
  return pathOnly.replace(/[^A-Za-z0-9_./:-]/g, "").slice(0, 191) || "/";
}

/**
 * Build a non-sensitive multipart issue payload for POST /api/issues.
 * Never includes raw error.message, stacks, SQL, credentials, or customer data.
 */
export function buildSalesSafeIssueReport(input: SalesSafeIssuePayloadInput): {
  title: string;
  description: string;
  urgency: "medium";
  pageUrl: string;
} {
  const surface = input.surface;
  const pathname =
    safePathname(input.pathname) ||
    (typeof window !== "undefined" ? safePathname(window.location.pathname) : "/");
  const pageUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${pathname}`
      : `https://app.local${pathname}`;
  const buildCommit = safeToken(
    input.buildCommit ?? (typeof window !== "undefined" ? getClientBuildCommit() : null),
    64,
  );
  const safeCode = safeToken(input.safeErrorCode, 64) || "UNKNOWN";
  const proposalRef = safeToken(input.allowlistedProposalRef, 80);
  const invoiceRefs = (input.allowlistedInvoiceRefs ?? [])
    .map((ref) => safeToken(ref, 48))
    .filter(Boolean)
    .slice(0, 8);
  const outstanding =
    input.allowlistedOutstandingSgd != null && Number.isFinite(Number(input.allowlistedOutstandingSgd))
      ? Math.round(Number(input.allowlistedOutstandingSgd) * 100) / 100
      : null;

  const lines = [
    "A Sales information request failed for a staff user.",
    "",
    "Allowlisted diagnostics:",
    `- surface: ${surface}`,
    `- safeErrorCode: ${safeCode}`,
    `- pathname: ${pathname}`,
    `- priorDataRetained: ${input.priorDataRetained ? "yes" : "no"}`,
  ];
  if (proposalRef) lines.push(`- proposalRef: ${proposalRef}`);
  if (invoiceRefs.length) lines.push(`- invoiceRefs: ${invoiceRefs.join(",")}`);
  if (outstanding != null) lines.push(`- apparentOutstandingSgd: ${outstanding.toFixed(2)}`);
  if (buildCommit && buildCommit !== "unknown") {
    lines.push(`- buildCommit: ${buildCommit}`);
  }

  return {
    title: SURFACE_TITLE[surface],
    description: lines.join("\n"),
    urgency: "medium",
    pageUrl,
  };
}

export async function submitSalesIssueReport(args: {
  surface: SalesIssueSurface;
  safeErrorCode: string;
  priorDataRetained: boolean;
  userName?: string | null;
  pathname?: string;
  signal?: AbortSignal;
  allowlistedProposalRef?: string | null;
  allowlistedInvoiceRefs?: readonly string[] | null;
  allowlistedOutstandingSgd?: number | null;
}): Promise<{ ok: true; id: number } | { ok: false }> {
  const built = buildSalesSafeIssueReport({
    surface: args.surface,
    safeErrorCode: args.safeErrorCode,
    priorDataRetained: args.priorDataRetained,
    pathname: args.pathname,
    allowlistedProposalRef: args.allowlistedProposalRef,
    allowlistedInvoiceRefs: args.allowlistedInvoiceRefs,
    allowlistedOutstandingSgd: args.allowlistedOutstandingSgd,
  });
  const form = new FormData();
  form.append("title", built.title);
  form.append("description", built.description);
  form.append("urgency", built.urgency);
  form.append("pageUrl", built.pageUrl);
  if (args.userName?.trim()) {
    form.append("userName", args.userName.trim().slice(0, 120));
  }

  try {
    const res = await fetch("/api/issues", {
      method: "POST",
      body: form,
      credentials: "include",
      signal: args.signal,
    });
    if (!res.ok) return { ok: false };
    const json = (await res.json()) as { id?: number };
    if (typeof json.id !== "number") return { ok: false };
    return { ok: true, id: json.id };
  } catch {
    return { ok: false };
  }
}
